import React, { useEffect, useRef, useState } from 'react';
import './utils/pwa.js';
import './styles/desktop.css';
import { AuthProvider, useAuthContext } from './contexts/AuthContext.js';
import { ProgressProvider, useProgressContext } from './contexts/ProgressContext.js';
import { UIProvider, useUIContext } from './contexts/UIContext.js'; // Corrected component name
import { OfflineProvider } from './contexts/OfflineContext.js';
import { NavigationProvider } from './contexts/NavigationContext.js';
import { LoadingProvider } from './contexts/LoadingContext.js';
import LoadingManager from './components/common/LoadingManager.js';
// import PWAInstallManager from './components/common/PWAInstallManager.js'; // DISABLED: Install prompts removed
import ErrorBoundary from './components/common/ErrorBoundary.js';
import ChunkLoadErrorBoundary from './components/common/ChunkLoadErrorBoundary.js';
import PageRouter from './PageRouter.js';
import ModalManager from './ModalManager.js';
import PWAStatus from './components/common/PWAStatus.js';
import CookieConsent from './components/common/CookieConsent.js';
import BugReportButton from './components/BugReportButton';
import { initializeGA4 } from './utils/analytics.js';
import { initializeVercelAnalytics } from './utils/vercelAnalytics.js';
import { initializeErrorTracking } from './utils/errorTracking.js';
import { initializePerformanceTracking } from './utils/performanceTracking.js';
import { trackSessionStarted } from './utils/speakrootsAnalytics.js';
import { useSwipeNavigation } from './hooks/useSwipeNavigation.js';
import { useSessionTimeout } from './hooks/useSessionTimeout.js';
import { initializeKeyboardHandler } from './utils/keyboardHandler.js';
import { isSignInWithEmailLink, signInWithEmailLink } from 'firebase/auth';
import { auth } from './api/firebase.js';
import logger from './utils/logger';
// import { initializeFontScaling, loadFontScalePreference } from './utils/fontScaling.js';

// Component to handle user interaction for audio
function AppContent() {
  const { handleUserInteraction, showFeedback, setPage } = useUIContext();
  const { completeOnboarding } = useProgressContext();
  const { user } = useAuthContext(); // Get authenticated user
  const hasSetUpInteraction = useRef(false);
  const magicLinkHandled = useRef(false);
  const isMountedRef = useRef(true); // 🔧 FIX: Track component mount status
  
  // Initialize swipe navigation for mobile
  useSwipeNavigation();
  
  // Initialize session timeout (30 minutes of inactivity)
  useSessionTimeout(30);

  // ✅ Epic 4: Magic Link Detection
  // Detects when user clicks magic link email and completes authentication
  // 🔧 FIX: Track component mount status
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      logger.info('App', 'App component unmounting');
    };
  }, []);
  
  useEffect(() => {
    const handleMagicLinkReturn = async () => {
      // Prevent multiple executions
      if (magicLinkHandled.current) return;
      magicLinkHandled.current = true;

      try {
        logger.info('MagicLink', 'Starting magic link authentication flow');
        
        // 🛡️ Set auth-in-progress flag IMMEDIATELY to prevent PageRouter from routing to onboarding
        localStorage.setItem('authInProgress', 'true');
        
        // 1. Get email from localStorage (saved when link was sent)
        let email = localStorage.getItem('emailForSignIn');
        
        // 2. Fallback: Get from URL params
        if (!email) {
          const urlParams = new URLSearchParams(window.location.search);
          email = urlParams.get('email');
        }
        
        // 3. Last resort: Prompt user to enter email
        if (!email) {
          email = window.prompt('Please provide your email for confirmation:');
        }
        
        // 4. Validate email
        if (!email || !email.includes('@')) {
          logger.error('MagicLink', 'Invalid email provided', { hasEmail: !!email });
          localStorage.removeItem('authInProgress');
          showFeedback('Invalid email. Please try signing up again.', 'error');
          setPage('prelesson_signup');
          return;
        }
        
        logger.info('MagicLink', 'Email validated, signing in user');
        showFeedback('Verifying your email...', 'success');
        
        // Show loading page during authentication
        setPage('loading');
        
        // 5. Complete sign-in with the link
        const result = await signInWithEmailLink(auth, email, window.location.href);
        const user = result.user;
        
        logger.info('MagicLink', 'User successfully signed in via magic link');
        
        // 6. Clear email from localStorage
        localStorage.removeItem('emailForSignIn');
        
        // 7. Get temp profile data and complete onboarding
        const tempProfileData = localStorage.getItem('tempProfileData');
        
        if (tempProfileData) {
          // NEW USER: Has temp profile data from sign-up flow
          const profileData = JSON.parse(tempProfileData);
          logger.info('MagicLink', 'New user detected, completing onboarding');
          
          try {
            // Complete onboarding with authenticated user
            const result = await completeOnboarding(
              profileData.nickname || profileData.name,
              profileData.ageGroup,
              profileData.language,
              user
            );
            
            // Check if this was a returning user (onboarding already completed)
            if (result?.alreadyCompleted) {
              logger.info('MagicLink', 'Returning user detected, onboarding already completed');
              
              // Clean up temp data immediately for returning users
              localStorage.removeItem('tempProfileData');
              logger.info('MagicLink', 'Cleaned up temp profile data for returning user');
              
              // 🔧 MOBILE FIX: Add delay to allow Firestore listener to receive data
              // On mobile networks, the Firestore listener can be slower
              await new Promise(resolve => setTimeout(resolve, 1500)); // 1.5 second delay
              
              // 🔧 FIX: Check if component is still mounted before navigating
              if (!isMountedRef.current) {
                logger.info('MagicLink', 'Component unmounted, aborting navigation');
                return;
              }
              
              // 🛡️ Clear auth-in-progress flag BEFORE navigation
              localStorage.removeItem('authInProgress');
              
              // Navigate to dashboard for returning users
              setPage('dashboard');
              showFeedback('Welcome back!', 'success');
              return;
            }
            
            // NEW USER - onboarding just completed
            // Set flag to skip orphan detection for next 60 seconds
            localStorage.setItem('justCompletedOnboarding', Date.now().toString());
            
            // 🔧 NEW: Delay temp data deletion by 60 seconds
            // This allows healing system to use it if needed
            setTimeout(() => {
              localStorage.removeItem('tempProfileData');
              logger.info('MagicLink', 'Delayed cleanup of temp profile data complete');
            }, 60000);  // 60 seconds
            
            logger.info('MagicLink', 'New user onboarding completed successfully');
            
            // 🔧 MOBILE FIX: Add delay to allow Firestore listener to receive data
            // On mobile networks, the Firestore listener can be slower
            // Wait for the progress data to be available before navigating
            await new Promise(resolve => setTimeout(resolve, 1500)); // 1.5 second delay
            
            // 🔧 FIX: Check if component is still mounted before navigating
            if (!isMountedRef.current) {
              logger.info('MagicLink', 'Component unmounted during onboarding, aborting navigation');
              return;
            }
            
            // 🛡️ Clear auth-in-progress flag BEFORE navigation
            localStorage.removeItem('authInProgress');
            
            // Navigate to lesson page for NEW users only
            setPage('lesson');
            showFeedback('Welcome! Your account is ready.', 'success');
          } catch (onboardingError) {
            // Unexpected error during onboarding
            logger.error('MagicLink', 'Onboarding failed during magic link flow', onboardingError);
            localStorage.removeItem('authInProgress');
            showFeedback('Setup failed. Please try again or contact support.', 'error');
            setPage('dashboard');
          }
        } else {
          // EXISTING USER: No temp profile data - they're logging in
          logger.info('MagicLink', 'Existing user logging in, loading progress data');
          
          // Show success message and stay on loading page
          // ProgressContext will load their data and trigger navigation via PageRouter
          showFeedback('Welcome back! Loading your progress...', 'success');
          
          // The authInProgress flag will be cleared by useProgress when data loads
          // PageRouter will show loading page while authInProgress is set
        }
        
      } catch (error) {
        logger.error('MagicLink', 'Magic link authentication failed', error);
        
        // Clear auth-in-progress flag on error
        localStorage.removeItem('authInProgress');
        
        let errorMessage = 'Sign-in failed. Please try again.';
        
        if (error.code === 'auth/invalid-action-code') {
          errorMessage = 'This link has expired or already been used. Please request a new one.';
        } else if (error.code === 'auth/invalid-email') {
          errorMessage = 'Email verification failed. Please try again.';
        }
        
        showFeedback(errorMessage, 'error');
        setPage('prelesson_signup');
      } finally {
        // Clean URL (remove query params) after all processing
        window.history.replaceState({}, document.title, window.location.pathname);
      }
    };

    // Check if the current URL is a sign-in link
    if (isSignInWithEmailLink(auth, window.location.href)) {
      logger.info('MagicLink', 'Magic link detected in URL, initiating authentication');
      handleMagicLinkReturn();
    }
  }, []); // Run once on mount

  // Browser history cleanup removed - using smart page mapping instead

  useEffect(() => {
    if (!hasSetUpInteraction.current) {
      hasSetUpInteraction.current = true;
      
      // Set up global click handler for user interaction
      const handleClick = () => {
        handleUserInteraction();
        // Remove the listener after first interaction
        document.removeEventListener('click', handleClick);
        document.removeEventListener('touchstart', handleClick);
      };

      document.addEventListener('click', handleClick);
      document.addEventListener('touchstart', handleClick);
      
      return () => {
        document.removeEventListener('click', handleClick);
        document.removeEventListener('touchstart', handleClick);
      };
    }
  }, [handleUserInteraction]);

      return (
        <>
          {/* Accessibility: Skip to main content link */}
          <a 
            href="#main-content" 
            className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:px-4 focus:py-2 focus:bg-blue-600 focus:text-white focus:rounded focus:shadow-lg"
          >
            Skip to main content
          </a>
          
          <PageRouter />
          <ModalManager />
          <PWAStatus />
          <CookieConsent />
          
          {/* Bug Report Button - Only show to authenticated users */}
          {user && <BugReportButton position="bottom-right" />}
        </>
      );
}

export default function App() {
  useEffect(() => {
    // Initialize all analytics tracking
    initializeGA4();
    initializeVercelAnalytics();
    initializeErrorTracking();
    initializePerformanceTracking();
    
    // Initialize keyboard handling for mobile
    const cleanupKeyboard = initializeKeyboardHandler();
    
    // Initialize font scaling for accessibility
    // const cleanupFontScaling = initializeFontScaling();
    // loadFontScalePreference();
    
    // Track session started
    trackSessionStarted();
    
    // Add PWA update detection to prevent loading loops
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        logger.info('APP', 'Service worker controller changed, clearing cached states');
        // Clear any cached loading states that might cause loops
        sessionStorage.removeItem('loading-state');
      });
    }

    // Global error handler for ChunkLoadErrors
    const handleGlobalError = (event) => {
      if (event.error && (
        event.error.name === 'ChunkLoadError' ||
        event.error.message.includes('Loading chunk') ||
        event.error.message.includes('Failed to load resource')
      )) {
        logger.error('App', 'Global ChunkLoadError detected', event.error);
        // The ChunkLoadErrorBoundary should catch this, but this is a fallback
        if (window.gtag) {
          window.gtag('event', 'exception', {
            description: 'Global ChunkLoadError: ' + event.error.message,
            fatal: false
          });
        }
      }
    };

    // Add global error listener
    window.addEventListener('error', handleGlobalError);
    
    // Add unhandled promise rejection listener for chunk loading failures
    const handleUnhandledRejection = (event) => {
      if (event.reason && (
        event.reason.name === 'ChunkLoadError' ||
        event.reason.message.includes('Loading chunk') ||
        event.reason.message.includes('Failed to load resource')
      )) {
        logger.error('App', 'Unhandled ChunkLoadError promise rejection', event.reason);
        if (window.gtag) {
          window.gtag('event', 'exception', {
            description: 'Unhandled ChunkLoadError: ' + event.reason.message,
            fatal: false
          });
        }
      }
    };

    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    
    logger.info('ANALYTICS', 'Analytics and error tracking initialized');
    
    // Cleanup
    return () => {
      window.removeEventListener('error', handleGlobalError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
      if (cleanupKeyboard) cleanupKeyboard();
      // if (cleanupFontScaling) cleanupFontScaling();
    };
  }, []);

  // --- START: Added logic for responsive layout ---
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 1024);

  useEffect(() => {
    const checkScreenSize = () => {
      setIsDesktop(window.innerWidth >= 1024);
    };
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  const containerClasses = isDesktop
    ? 'w-full min-h-screen bg-gray-50' // Full-width styles for desktop
    : 'max-w-md mx-auto min-h-screen bg-white shadow-2xl rounded-lg'; // Centered, narrow styles for mobile
  // --- END: Added logic for responsive layout ---

  return (
    <div className={containerClasses}>
      <ErrorBoundary>
        <ChunkLoadErrorBoundary>
          <AuthProvider>
            <ProgressProvider>
              <UIProvider>
                <NavigationProvider>
                  <OfflineProvider>
                    <LoadingProvider>
                      <LoadingManager>
                        <AppContent />
                        {/* <PWAInstallManager /> DISABLED: Install prompts removed */}
                      </LoadingManager>
                    </LoadingProvider>
                  </OfflineProvider>
                </NavigationProvider>
              </UIProvider>
            </ProgressProvider>
          </AuthProvider>
        </ChunkLoadErrorBoundary>
      </ErrorBoundary>
    </div>
  );
}
